import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  CalendarCheck,
  Wallet,
  PhoneCall,
  Receipt,
  Mic,
  Lock,
  CalendarClock,
  BarChart3,
  Bug,
  Wind,
  Home as HomeIcon,
  Hammer,
  Bath,
  AppWindow,
  Ruler,
  Fence,
  Check,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import MobileCtaBar from "@/components/site/MobileCtaBar";
import { BOOKING_URL, SectionHead, fadeUp, stagger } from "@/components/site/shared";

/* ---------- HERO ---------- */
const AppointmentRow = ({ day, num, service, time, status = "booked", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -16 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0"
  >
    <div className="font-mono text-[10px] tracking-widest text-white/50 bg-white/5 rounded-md px-2 py-1 min-w-[52px] text-center">
      {day} {num}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] text-white font-medium truncate">{service}</div>
      <div className="text-[11px] text-white/50 truncate">Booked · qualified</div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
      <div className="font-mono text-[11px] text-white/60">{time}</div>
    </div>
  </motion.div>
);

const HeroCalendarCard = () => (
  <div className="relative w-full max-w-[440px] arco-float">
    <div className="absolute -inset-8 bg-[#FF5C00]/20 blur-3xl rounded-[3rem] -z-10" />
    <div
      className="relative rounded-2xl border border-white/10 bg-[#141F3D]/90 backdrop-blur-2xl overflow-hidden shadow-[0_50px_120px_-30px_rgba(255,92,0,0.35)]"
      data-testid="hero-calendar-card"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FF5C00]/15 flex items-center justify-center">
            <CalendarCheck className="w-4 h-4 text-[#FF5C00]" />
          </div>
          <div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest font-mono">
              This week
            </div>
            <div className="text-white font-semibold text-sm">Booked Appointments</div>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-[#FF5C00]/15 text-[#FF5C00] rounded-full px-3 py-1 text-xs font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
          10 / 10
        </div>
      </div>
      <div className="py-1">
        <AppointmentRow day="MON" num="14" service="Pest control quote" time="9:00 AM" delay={0.4} />
        <AppointmentRow day="MON" num="14" service="HVAC service call" time="1:30 PM" delay={0.5} />
        <AppointmentRow day="TUE" num="15" service="Roof inspection" time="10:00 AM" delay={0.6} />
        <AppointmentRow day="WED" num="16" service="Window quote" time="8:30 AM" delay={0.7} />
        <AppointmentRow day="THU" num="17" service="Bathroom remodel" time="4:00 PM" delay={0.8} />
      </div>
      <div className="flex items-center justify-between px-5 py-4 border-t border-white/10 bg-black/30">
        <div className="flex items-center gap-2 text-[11px]">
          <Mic className="w-3.5 h-3.5 text-[#FF5C00]" />
          <span className="text-white/70">
            <span className="text-white font-semibold">Every call</span> recorded &amp; transcribed
          </span>
        </div>
        <button className="text-[11px] text-[#FF5C00] font-semibold hover:underline">
          open dashboard
        </button>
      </div>
    </div>
  </div>
);

const Hero = () => (
  <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
    <div className="absolute inset-0 arco-hero-glow pointer-events-none" />
    <div className="absolute inset-0 arco-dots opacity-70 pointer-events-none" />
    <div className="absolute inset-0 arco-grain pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-10 items-center">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <motion.div
          variants={fadeUp}
          className="inline-flex items-center gap-2 rounded-full border border-[#FF5C00]/30 bg-[#FF5C00]/8 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5C00]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
          Built for busy local operators · Pay-Per-Call
        </motion.div>

        <motion.h1
          variants={fadeUp}
          data-testid="appointments-headline"
          className="font-display mt-6 text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] font-black leading-[0.95] tracking-tighter"
        >
          Stop buying leads.
          <br />
          <span
            className="text-[#FF5C00] italic font-black"
            style={{ fontFamily: '"Cabinet Grotesk", serif' }}
          >
            Start buying
          </span>{" "}
          <span className="text-white">booked appointments.</span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          className="mt-7 text-white/70 text-lg md:text-xl leading-relaxed max-w-xl"
        >
          Pay-Per-Call. We deliver qualified, booked appointments straight to your
          calendar. A real customer called, got qualified, and booked a time. You
          only pay when a call turns into a booked job.{" "}
          <span className="text-white font-semibold">No retainers, no ad spend, no shared leads.</span>
        </motion.p>

        <motion.div variants={fadeUp} className="mt-9 flex flex-wrap items-center gap-3">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="book-call-btn-appt-hero"
            className="inline-flex arco-btn-primary items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
          >
            Book a Call <ArrowRight className="w-4 h-4" />
          </a>
          <a
            href="#how"
            data-testid="how-it-works-btn-appt"
            className="inline-flex arco-btn-outline items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
          >
            See How It Works
          </a>
        </motion.div>

        <motion.ul
          variants={fadeUp}
          className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 max-w-xl"
        >
          {[
            "Pay-Per-Call Only",
            "No Retainers",
            "Every Call Recorded",
            "Exclusively Yours",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2 text-white/75 text-[13px]">
              <Check className="w-4 h-4 text-[#FF5C00] shrink-0 mt-0.5" />
              {t}
            </li>
          ))}
        </motion.ul>
      </motion.div>

      <div className="flex justify-center lg:justify-end">
        <HeroCalendarCard />
      </div>
    </div>
  </section>
);

/* ---------- HOW IT WORKS ---------- */
const howSteps = [
  {
    n: "01",
    title: "A real customer calls",
    body:
      "Live inbound calls from customers in your area searching for your service are routed straight to your phone first. You answer like you always do.",
    Icon: PhoneCall,
  },
  {
    n: "02",
    title: "The appointment gets booked",
    body:
      "You answer, qualify the caller, and book the job. Miss the call? Our appt setter answers instantly, qualifies the caller, and books the appointment into your calendar so the customer never slips away.",
    Icon: CalendarCheck,
  },
  {
    n: "03",
    title: "You only pay for what books",
    body:
      "If 100 calls come in and 20 turn into booked appointments, you pay for 20. Every call is recorded and transcribed in your dashboard, so you can hear exactly what you paid for.",
    Icon: Receipt,
  },
];

const HowItWorks = () => (
  <section id="how" className="relative py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="How it works"
        title="Calls come in."
        accent="Appointments go out."
        sub="A simple, transparent flow that turns real inbound calls into paid appointments on your calendar."
      />
      <div className="mt-20 grid md:grid-cols-3 gap-6 relative">
        <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        {howSteps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="arco-card rounded-2xl p-8 relative"
            data-testid={`how-card-${i + 1}`}
          >
            <div className="flex items-start justify-between mb-6">
              <span
                className="font-display text-6xl md:text-7xl font-black leading-none"
                style={{
                  background: "linear-gradient(180deg, rgba(255,92,0,1), rgba(255,92,0,0.25))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {s.n}
              </span>
              <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/12 border border-[#FF5C00]/25 flex items-center justify-center">
                <s.Icon className="w-5 h-5 text-[#FF5C00]" />
              </div>
            </div>
            <h3 className="font-display text-white text-2xl font-bold tracking-tight mb-3">
              {s.title}
            </h3>
            <p className="text-white/60 text-[15px] leading-relaxed">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- WHY THIS BEATS LEADS ---------- */
const whyBeats = [
  {
    title: "Appointments, not phone numbers",
    body:
      "A lead is a name you have to chase. An appointment is a customer with a time slot on your calendar.",
    Icon: CalendarClock,
  },
  {
    title: "Pay only for results",
    body:
      "No monthly retainer, no ad budget, no setup fees. Your money buys booked appointments and nothing else.",
    Icon: Wallet,
  },
  {
    title: "Every call on record",
    body:
      "Each appointment comes with the full call recording and transcript in your dashboard. No arguing over quality, you can listen for yourself.",
    Icon: Mic,
  },
  {
    title: "Exclusively yours",
    body:
      "No lead is ever sold to you and four competitors. The customer called, qualified, and booked with you.",
    Icon: Lock,
  },
  {
    title: "Your calendar fills itself",
    body:
      "Missed calls stop costing you jobs. If you cannot pick up, the appointment still gets booked.",
    Icon: CalendarCheck,
  },
  {
    title: "Scale up or down",
    body:
      "Buy the volume your crew can handle. Start small, prove it works, then increase your weekly block.",
    Icon: BarChart3,
  },
];

const WhyBeatsLeads = () => (
  <section className="relative py-28 md:py-36 bg-[#0A1329]">
    <div className="absolute inset-0 arco-dots opacity-40 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <SectionHead
        eyebrow="Why this beats buying leads"
        title="No chasing."
        accent="Just calendars filling."
        sub="Six reasons operators quit shared leads and monthly retainers for pay-per-appointment."
      />
      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {whyBeats.map((w, i) => (
          <motion.div
            key={w.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.06 }}
            className="arco-card rounded-2xl p-7 group"
            data-testid={`appt-why-card-${i + 1}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-5 group-hover:bg-[#FF5C00]/20 transition-colors">
              <w.Icon className="w-5 h-5 text-[#FF5C00]" />
            </div>
            <h3 className="font-display text-white text-xl font-bold mb-2 tracking-tight">
              {w.title}
            </h3>
            <p className="text-white/55 text-[14px] leading-relaxed">{w.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- WHO IT IS FOR ---------- */
const trades = [
  { name: "Pest control", Icon: Bug },
  { name: "HVAC", Icon: Wind },
  { name: "Roofing", Icon: HomeIcon },
  { name: "Remodeling", Icon: Hammer },
  { name: "Bathrooms", Icon: Bath },
  { name: "Windows", Icon: AppWindow },
  { name: "Gutters", Icon: Ruler },
  { name: "Fencing", Icon: Fence },
];

const WhoFor = () => (
  <section className="relative py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="Who it is for"
        title="Built for shops that can"
        accent="handle new jobs daily."
        sub="This is built for businesses that can take on new jobs every single day."
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        {trades.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
            className="arco-card rounded-xl p-5 flex flex-col items-center text-center gap-3"
            data-testid={`trade-${i + 1}`}
          >
            <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center">
              <t.Icon className="w-5 h-5 text-[#FF5C00]" />
            </div>
            <div className="font-display text-white text-base font-bold tracking-tight">
              {t.name}
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        className="mt-14 max-w-2xl mx-auto text-center text-white/70 text-[15px] md:text-base border-t border-white/10 pt-10"
      >
        You should be able to handle at least{" "}
        <span className="text-white font-semibold">5 new appointments a day, 5 days a week</span>.
        If your phones are already overflowing, this is not for you.
      </motion.div>
    </div>
  </section>
);

/* ---------- PRICING (straight talk) ---------- */
const Pricing = () => (
  <section className="relative py-28 md:py-36 bg-[#0A1329]">
    <div className="max-w-4xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="Straight talk on pricing"
        title="Prepaid blocks."
        accent="No surprises."
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="mt-14 grid md:grid-cols-2 gap-5"
      >
        <div className="arco-card rounded-2xl p-8" data-testid="pricing-card-1">
          <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-5">
            <Receipt className="w-5 h-5 text-[#FF5C00]" />
          </div>
          <h3 className="font-display text-white text-2xl font-bold mb-3 tracking-tight">
            Priced by trade &amp; area
          </h3>
          <p className="text-white/60 text-[15px] leading-relaxed">
            Pricing is per appointment and depends on your trade and service area. On
            your fit call we quote your exact area, no obligation.
          </p>
        </div>
        <div className="arco-card rounded-2xl p-8" data-testid="pricing-card-2">
          <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-5">
            <CalendarClock className="w-5 h-5 text-[#FF5C00]" />
          </div>
          <h3 className="font-display text-white text-2xl font-bold mb-3 tracking-tight">
            10 minimum per week
          </h3>
          <p className="text-white/60 text-[15px] leading-relaxed">
            Appointments are purchased in prepaid weekly blocks with a minimum of
            <span className="text-white font-semibold"> 10 appointments per week, 2 per day</span> across the work week.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const faqs = [
  {
    q: "What counts as a billable appointment?",
    a: "A real customer called, was qualified for your service and area, and a time was booked. Calls that do not qualify or do not book cost you nothing.",
  },
  {
    q: "What if the customer does not show up or cancels?",
    a: "Every call is recorded and transcribed in your dashboard. If an appointment does not meet the qualification standard, flag it and we review the recording together.",
  },
  {
    q: "Do I need to run ads or pay a retainer?",
    a: "No. There is no ad spend, no retainer, and no setup fee. You prepay for a weekly block of appointments and that is the whole cost.",
  },
  {
    q: "How fast does it start?",
    a: "Once your service area is confirmed and your first block is active, appointments can start flowing within days, not months.",
  },
  {
    q: "Can I use this together with the Google ranking service?",
    a: "Yes. Ranking your Google Business Profile builds a long term asset. Pay per appointment fills your calendar right now. Most operators run both.",
  },
];

const FAQ = () => (
  <section id="appt-faq" className="relative py-28 md:py-36">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="FAQ"
        title="Questions,"
        accent="answered."
        sub="Straight answers to what every operator asks before their first block."
      />
      <div className="mt-14">
        <Accordion type="single" collapsible defaultValue="item-0" className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border border-white/10 rounded-xl bg-[#141F3D] px-6 [&[data-state=open]]:border-[#FF5C00]/40 [&[data-state=open]]:bg-[#1B274A] transition-all"
            >
              <AccordionTrigger
                data-testid={`appt-faq-trigger-${i}`}
                className="text-left text-white font-semibold text-[15px] md:text-base hover:no-underline py-5 [&[data-state=open]]:text-[#FF5C00] [&>svg]:text-white/60"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-[14px] md:text-[15px] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

/* ---------- FINAL CTA ---------- */
const FinalCTA = () => (
  <section className="relative py-32 md:py-44 overflow-hidden">
    <div className="absolute inset-0 arco-cta-glow pointer-events-none" />
    <div className="absolute inset-0 arco-dots opacity-40 pointer-events-none" />
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="font-display text-white text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter leading-[0.9]"
      >
        Your calendar,
        <br />
        <span
          className="text-[#FF5C00] italic"
          style={{ fontFamily: '"Cabinet Grotesk", serif' }}
        >
          full.
        </span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-8 text-white/70 text-base md:text-lg max-w-xl mx-auto"
      >
        Book a quick fit call. We will quote your service area and show you the
        dashboard where every call, recording, and appointment lives.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="mt-10 flex flex-col items-center gap-4"
      >
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="book-call-btn-appt-cta"
          className="inline-flex arco-btn-primary items-center gap-2 rounded-full px-8 py-4 text-base font-semibold"
        >
          Book a Call <ArrowRight className="w-5 h-5" />
        </a>
        <p className="text-white/40 text-xs max-w-md">
          Volume is limited per market. Service areas are claimed first come, first served.
        </p>
      </motion.div>
    </div>
  </section>
);

/* ---------- PAGE ---------- */
export default function Appointments() {
  return (
    <main
      data-testid="appointments-page"
      className="min-h-screen bg-[#0C1732] text-white overflow-x-hidden"
    >
      <Nav />
      <Hero />
      <HowItWorks />
      <WhyBeatsLeads />
      <WhoFor />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
      <MobileCtaBar />
    </main>
  );
}
